import React, {useState} from 'react';
import {useIntl} from 'react-intl';
import {CrossIcon, InputLabel, WarningIcon} from 'web-components';
import _ from 'lodash';
import {
	FlexGroup,
	HoverShow,
	Item,
	ItemDescription,
	ItemName,
	ItemNote,
	ItemWarning,
	PreviewSelector,
	Selections,
	SelectionsHeader,
	SelectionsHeaderActions,
} from './PreviewSelector.styled';
import {Scrollbars} from 'react-custom-scrollbars';
import Search from '../../../../../components/new-ui/search';
import {createFragmentContainer, graphql} from 'react-relay';
import SkillDropdown from "../skill-dropdown/SkillDropdown";

const SkillsPreviewSelector = ({skills, skillCategories, selectedSkills, onChange, warningCallback = () => false, selectionHeaderLabel, hideSelectionHeaderActions = false}) => {
	const {formatMessage} = useIntl();
	const noCategory = formatMessage({id: 'settings_skills.no_category'});

	const skillById = _.keyBy(skills, skill => skill.id);
	const [searchValue, setSearchValue] = useState('');

	if (!selectionHeaderLabel) {
		selectionHeaderLabel = formatMessage({id: `common.selected`});
	}

	const getSearchString = skill =>
		[skill.name, skill.category?.name].map(string => string?.toLowerCase()).join(' ');

	const searchFilter = skill => {
		return getSearchString(skill).includes(searchValue.toLowerCase());
	};

	const selected = selectedSkills
		.map(selectedSkill => skillById[selectedSkill])
		.filter(Boolean)
		.filter(searchFilter)
		.sort((a, b) => {
			const aWarning = !!warningCallback(a);
			const bWarning = !!warningCallback(b);
			return aWarning === bWarning ? 0 : aWarning ? -1 : 1;
		});

	const onSkillSelectionUpdate = (_, skillIds) => {
		onChange(skillIds);
	};

	const onRemove = skill => {
		onChange(selectedSkills.filter(item => item !== skill.id));
	};

	return (
		<PreviewSelector>
			<InputLabel
				text={formatMessage({id: 'common.skills'})}
				child={
					<SkillDropdown
						usePortal
						name={formatMessage({id: 'settings_skills.add_skills'})}
						skills={skills}
						skillCategories={skillCategories}
						selectedItems={selectedSkills}
						onSelect={onSkillSelectionUpdate}
						onRemove={onSkillSelectionUpdate}
						selectedGroupName={null}
					/>
				}
			/>
			<Selections>
				<SelectionsHeader>
					<InputLabel text={`${selectionHeaderLabel} (${selected.length})`} />
					{!hideSelectionHeaderActions && (
						<SelectionsHeaderActions>
							<Search key={`selected-search`} value={searchValue} onChange={setSearchValue} />
						</SelectionsHeaderActions>
					)}
				</SelectionsHeader>
				<Scrollbars style={{height: '205px'}}>
					{selected.map(skill => {
						const warning = warningCallback(skill);
						return (
							<Item key={skill.id}>
								<FlexGroup>
									{!!warning &&
										<WarningIcon size={WarningIcon.SIZE.STANDARD} updated color={'#E01212'}/>}
									<ItemDescription>
										<ItemName>{skill.name}</ItemName>
										{warning ? (
											<ItemWarning>{warning}</ItemWarning>
										) : (
											<ItemNote>{skill.category?.name || noCategory}</ItemNote>
										)}
									</ItemDescription>
								</FlexGroup>
								<HoverShow>
									<CrossIcon size={CrossIcon.SIZE.STANDARD} color={'#000'}
											   onClick={() => onRemove(skill)}/>
								</HoverShow>
							</Item>
						);
					})}
				</Scrollbars>
			</Selections>
		</PreviewSelector>
	);
};
export default createFragmentContainer(SkillsPreviewSelector, {
	skillCategories: graphql`
        fragment SkillsPreviewSelector_skillCategories on SkillCategory @relay(plural: true) {
            ...SkillDropdown_skillCategories
        }
	`,
	skills: graphql`
        fragment SkillsPreviewSelector_skills on Skill @relay(plural: true) {
            ...SkillDropdown_skills
            id
            name
            category {
                id
                name
            }
        }
	`,
});
